import _ from 'underscore';
import Promise from 'bluebird';
import React from 'react';
import { HelpPopup } from '@bingads-webui-react/help-popup';

import {
  CHANNEL_TYPES,
  ADINSIGHT_LOG_ACTION_TYPE,
  RECOMMENDATION_TYPES,
  SETTING_OPTIONS,
  DATE_FILTER,
  RECOMMENDATION_CATEGORIES,
} from '@bingads-webui-campaign/recommendation-core';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { levelAtConstant } from '@bingads-webui-campaign/scope-constants';
import { showDismissFeedbackPopup } from '@bingads-webui-campaign-react/recommendation-dismiss-feedback';
import {
  showApplyFeedbackPopup,
  showRejectFeedbackPopup,
} from '@bingads-webui-campaign-react/recommendation-feedback-modal';
import { generateGuid } from '@bingads-webui-universal/primitive-utilities';
import classNames from 'classnames';
import { filterBySettingOptions } from './filter-helper';

import {
  SUPPORT_APPLY_TYPES,
  SUPPORT_DOWNLOAD_TYPES,
  SUPPORT_DISMISS_TYPES,
  SUPPORT_RESUBMIT_TYPES,
  SUPPORT_DATE_FILTER_TYPES,
  DATE_FILTER_STRING_TYPES,
  SUPPORT_DISMISS_ALL_TYPES,
} from './const';

function getSelectedOpportunities(gridView) {
  const recommendations = gridView.selectedItems();
  const opportunities = _.chain(recommendations)
    .map(i => i.Opportunities)
    .flatten().value();

  return opportunities;
}

function showAutoApplyDismissFeedbackForm(
  i18n,
  recommendationType,
  gridService,
  deps
) {
  showDismissFeedbackPopup({
    i18n,
    type: recommendationType,
    onSubmit: (data) => {
      gridService.dismissFeedback({
        data,
        deps,
        rejectAutoApplySuggestedAds: true,
      });
    },
    rejectAutoApplySuggestedAds: true,
    permissions: deps.permissions,
  });
}

function updateCustomizeToolbarDropdownText(gridToolbarGroup, options) {
  const iconClass = 'ba-CheckMark';
  const iconLeft = 'iconLeftGlyphName';

  const updateToolbarDateEditor = (buttonText) => {
    gridToolbarGroup.toolbarWithItem(options.id).update({
      id: options.id,
      button: {
        text: buttonText,
      },
    });

    _.map(options.items, (item) => {
      gridToolbarGroup.toolbarWithItem(item.id).update({
        id: item.id,
        [iconLeft]: buttonText === item.buttonText && iconClass,
        onClick: () => {
          if (buttonText !== item.buttonText) {
            item.onClick();
            updateToolbarDateEditor(item.buttonText);
          }
        },
      });
    });
  };

  updateToolbarDateEditor(options.defaultButtonText);
}

function autoApplyDialog({
  i18n,
  deps,
  gridService,
  recommendationType,
  context,
  guid,
  view,
  callback,
  newI18n,
  isCrossPageApplyAll,
}) {
  const { scope } = gridService.recommendationService;
  const level = scope ? scope.levelAt : levelAtConstant.ACCOUNT;
  const adGroupIdVNext = scope && scope.adGroupId ? [scope.adGroupId] : [];
  const campaignAdGroups = level !== levelAtConstant.ACCOUNT ? [{
    Key: scope.campaignId,
    Value: adGroupIdVNext,
  }] : [];
  const options = {
    i18n,
    dataService: gridService.recommendationService,
    el: '.recommendation-apply-feedback-popup',
    data: { type: recommendationType, channel: CHANNEL_TYPES.RECOMMENDATION, campaignAdGroups },
    guid,
    context,
    callback,
    cid: scope.advertiserCustomerId,
    aid: scope.accountId,
    odata: deps.odata,
    onAutoApplyCancel: () => {
      showAutoApplyDismissFeedbackForm(i18n, recommendationType, gridService, deps);
    },
    view,
    useCallbackAsSubmit: true,
    permissions: deps.permissions,
    newI18n,
    isCrossPageApplyAll,
    showApplyConfirmModal: _.partial(callback, true),
  };
  showApplyFeedbackPopup(options);
}

export const recommendationToolBarPlugin = definePlugin => definePlugin('recommendationToolBarPlugin', [
  'config',
  'toolbar',
  'gridView',
  'crossPageSelectAll',
  'recommendationInlineAction',
], ({
  plugins: {
    recommendationToolBarPlugin: {
      i18n,
      deps,
      gridService,
      recommendationType,
      campaignType,
      downloadService,
      logOpportunityEvent,
      context,
      guid,
      view,
      el,
      readOnly,
      onEvent,
      isMCC = false,
      channel,
      compact = false,
      viewDetails,
      recommendationTypesAvailable,
      newI18n,
    },
  },
}, {
  gridToolbarGroup,
}, gridView, crossPageSelectAll, recommendationInlineAction) => {
  const isInProdFeedbackLoopOn = deps.permissions.IsRecommendationInProductFeedbackLoopEnabled;
  const isRedismissEnabled = deps.permissions.IsRecommendationRedismissEnabled;
  const isOptimizationScoreOn = !isMCC && channel === CHANNEL_TYPES.RECOMMENDATION;
  const isDismissAll = isInProdFeedbackLoopOn || isOptimizationScoreOn;
  const showRejectPopup = () => {
    const { scope } = gridService.recommendationService;
    const level = scope ? scope.levelAt : levelAtConstant.ACCOUNT;
    const adGroupIdVNext = scope && scope.adGroupId ? [scope.adGroupId] : [];
    const campaignAdGroups = level !== levelAtConstant.ACCOUNT ? [{
      Key: scope.campaignId,
      Value: adGroupIdVNext,
    }] : [];
    showRejectFeedbackPopup({
      i18n,
      newI18n,
      dataService: gridService.recommendationService,
      el: '.recommendations-page',
      data: {
        type: recommendationType,
        channel: channel
          || (isMCC ? CHANNEL_TYPES.MCCRECOMMENDATION : CHANNEL_TYPES.RECOMMENDATION),
        campaignAdGroups,
      },
      guid,
      context,
      callback: () => {
        onEvent('refreshSummary', { isFromApplyOrDismiss: true });
      },
      readOnly,
      isDismissAll,
      permissions: deps.permissions,
      viewDetails,
      recommendationTypesAvailable,
    });
  };

  const applyInToolbar = (showApplyAllComfirmModal = false) => {
    const inlineView = recommendationInlineAction.view;
    const opportunities = getSelectedOpportunities(gridView);
    // apply all opportunity cross page
    if (crossPageSelectAll.isAllSelected) {
      const applyConfirmPromise = new Promise((resolve) => {
        if (showApplyAllComfirmModal) {
          onEvent('showApplyConfirmModal', { onApply: resolve, countRecommendation: gridView.totalCountRows });
        } else {
          resolve();
        }
      });

      return applyConfirmPromise
        .then(() => withUiBlock(el))
        .then(closeUiBlockApplyAll =>
          gridService.applyAll({
            recommendationType,
            opportunities,
            closeUiBlock: closeUiBlockApplyAll,
            // opportunity is the base unit of apply
            totalRecommendationCount: gridService.opportunityCount,
          }))
        .then(() => {
          gridView.refresh({ force: true });
        });
    }

    // apply the only one expanded inline view.
    if (inlineView && inlineView.childView && inlineView.childView.apply) {
      inlineView.childView.apply();
      return null;
    }

    // apply selected opportunities
    // filter out unwanted opportunities for special cases
    let filteredOpportunities = opportunities;
    if (recommendationType === RECOMMENDATION_TYPES.FIX_AD_DESTINATION) {
      filteredOpportunities = _.filter(opportunities, opportunity => opportunity.IsAppealable);
    }

    return gridService.apply({
      opportunities: filteredOpportunities,
      closeUiBlock: withUiBlock(el),
    }).then(() => {
      gridView.refresh({ force: true });
    });
  };

  if (_.contains(SUPPORT_APPLY_TYPES, recommendationType)
    || _.contains(SUPPORT_RESUBMIT_TYPES, recommendationType)) {
    gridToolbarGroup.toolbarWithItem('button-apply').update({
      id: 'button-apply',
      onClick: () => {
        const opportunities = getSelectedOpportunities(gridView);

        const rejectAutoApplySuggestedAds = true;
        const isShowFeedback = gridService.isShowFeedback({
          recommendationType,
          deps,
          rejectAutoApplySuggestedAds,
          permissions: deps.permissions,
        });

        if (recommendationType === RECOMMENDATION_TYPES.IMAGE_EXTENSION
          && _.some(opportunities, opporunity => opporunity.ImageSourceStr === 'Website')
          && deps.permissions.IsAccountEnabledForCrawlImagesFromLandingPage) {
          const { scope } = gridService.recommendationService;
          const level = scope ? scope.levelAt : levelAtConstant.ACCOUNT;
          const adGroupIdVNext = scope && scope.adGroupId ? [scope.adGroupId] : [];
          const campaignAdGroups = level !== levelAtConstant.ACCOUNT ? [{
            Key: scope.campaignId,
            Value: adGroupIdVNext,
          }] : [];
          const options = {
            odata: deps.odata,
            cid: scope.advertiserCustomerId,
            aid: scope.accountId,
            permissions: deps.permissions,
            isFromImgExtV2: true,
            i18n,
            el: '.recommendation-apply-feedback-popup',
            data: {
              type: recommendationType,
              channel: CHANNEL_TYPES.RECOMMENDATION,
              campaignAdGroups,
            },
            dataService: gridService.recommendationService,
            callback: applyInToolbar,
            newI18n,
          };
          showApplyFeedbackPopup(options);
          return null;
        }
        // Condition: Apply all new ads recommendations
        // Show a dialog to confirm enabling auto-apply ad suggestions.
        if (!isMCC
          && (recommendationType === RECOMMENDATION_TYPES.RESPONSIVE_SEARCH_ADS
              && deps.permissions.IsRSAAutoApplyEnabled)
          && (crossPageSelectAll.isAllSelected || opportunities.length === gridView.totalCountRows)
          && isShowFeedback) {
          autoApplyDialog({
            i18n,
            deps,
            gridService,
            recommendationType,
            context,
            guid,
            view,
            callback: applyInToolbar,
            newI18n,
            isCrossPageApplyAll: crossPageSelectAll.isAllSelected,
          });
          return null;
        }

        return applyInToolbar(true);
      },
    });
  }

  if (_.contains(SUPPORT_DISMISS_TYPES, recommendationType)
    && _.result(context, 'Category', '') === RECOMMENDATION_CATEGORIES.DISMISSED) {
    gridToolbarGroup.toolbarWithItem('button-undismiss').update({
      id: 'button-undismiss',
      onClick: (e) => {
        e.stopPropagation();
        const trackId = generateGuid();
        const inlineView = recommendationInlineAction.view;
        const opportunities = getSelectedOpportunities(gridView);

        if (crossPageSelectAll.isAllSelected) {
          gridService.undismissAll({ recommendationType, context: { trackId } }).then(() => {
            gridView.refresh({ force: true });
          }).finally(withUiBlock(el));
        } else {
          // dismiss the only one expanded inline view.
          if (inlineView && inlineView.childView && inlineView.childView.dismiss) {
            inlineView.childView.dismiss({ context: { trackId } });
            return null;
          }

          // dismiss selected opportunities
          gridService.undismiss({ opportunities, context: { trackId } }).then(() => {
            gridView.refresh({ force: true });
          }).finally(withUiBlock(el));
        }

        return true;
      },
    });
  }

  if (_.contains(SUPPORT_DISMISS_TYPES, recommendationType)
    && _.result(context, 'Category', '') !== RECOMMENDATION_CATEGORIES.DISMISSED) {
    gridToolbarGroup.toolbarWithItem('button-dismiss').update({
      id: 'button-dismiss',
      onClick: (e) => {
        e.stopPropagation();
        const trackId = generateGuid();
        const rejectAutoApplySuggestedAds = false;
        const isShowFeedback = gridService.isShowFeedback({
          recommendationType,
          deps,
          rejectAutoApplySuggestedAds,
          permissions: deps.permissions,
        });
        const inlineView = recommendationInlineAction.view;
        const opportunities = getSelectedOpportunities(gridView);

        if (crossPageSelectAll.isAllSelected) {
          if (isRedismissEnabled) {
            showRejectPopup();
          } else {
            gridService.dismissAll({ recommendationType, context: { trackId } }).then(() => {
              gridView.refresh({ force: true });
            }).finally(withUiBlock(el));
          }
        } else {
          // dismiss the only one expanded inline view.
          if (inlineView && inlineView.childView && inlineView.childView.dismiss) {
            inlineView.childView.dismiss({ context: { trackId } });
            return null;
          }

          // dismiss selected opportunities
          gridService.dismiss({ opportunities, context: { trackId } }).then(() => {
            gridView.refresh({ force: true });
          }).finally(withUiBlock(el));
        }

        // show the dismiss feedback
        if (isShowFeedback && !(isRedismissEnabled && crossPageSelectAll.isAllSelected)) {
          showDismissFeedbackPopup({
            newI18n,
            i18n,
            type: recommendationType,
            onSubmit: (data, isShowNewDismissForm) => {
              gridService.dismissFeedback({
                data,
                deps,
                context: { trackId },
                opportunities,
                isRedismissEnabled: isRedismissEnabled && isShowNewDismissForm,
              });
            },
            permissions: deps.permissions,
            viewDetails,
            recommendationTypesAvailable,
          });
        }

        return true;
      },
    });
  }

  if (_.contains(SUPPORT_DATE_FILTER_TYPES, recommendationType)) {
    updateCustomizeToolbarDropdownText(
      gridToolbarGroup,
      {
        id: 'dropdown-date-filter',
        defaultButtonText: i18n.getString(DATE_FILTER_STRING_TYPES.LAST_SIX_MONTH),
        items: [
          {
            id: 'dropdown-item-lastthreemonth',
            buttonText: i18n.getString(DATE_FILTER_STRING_TYPES.LAST_THREE_MONTH),
            onClick: () => {
              gridService.updateFilterState({ dateFilter: DATE_FILTER.LAST_90 });
              filterBySettingOptions(gridView, {
                recommendationType,
                addedOptions: SETTING_OPTIONS.REJECTION_DATE_LAST_NINETY_DAYS,
              });
              if (view.grid.pagination) {
                view.grid.pagination.trigger('change:page-size');
              }
            },
          },
          {
            id: 'dropdown-item-lastsixmonth',
            buttonText: i18n.getString(DATE_FILTER_STRING_TYPES.LAST_SIX_MONTH),
            onClick: () => {
              gridService.updateFilterState({ dateFilter: DATE_FILTER.LAST_180 });
              filterBySettingOptions(gridView, {
                recommendationType,
                addedOptions: SETTING_OPTIONS.REJECTION_DATE_LAST_NINETY_DAYS,
                isRemove: true,
              });
              if (view.grid.pagination) {
                view.grid.pagination.trigger('change:page-size');
              }
            },
          },
        ],
      }
    );
  }

  if (_.contains(SUPPORT_DOWNLOAD_TYPES, recommendationType)) {
    gridToolbarGroup.toolbarWithItem('button-download').update({
      id: 'button-download',
      onClick: () => {
        downloadService.download({
          channel: channel
            || (isMCC ? CHANNEL_TYPES.MCCRECOMMENDATION : CHANNEL_TYPES.RECOMMENDATION),
          recommendationType,
          campaignType,
        }).then(() => {
          logOpportunityEvent({
            type: recommendationType,
            action: ADINSIGHT_LOG_ACTION_TYPE.DOWNLOAD,
          });
        });
      },
    });
  }

  if (!compact) {
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    const rejectHelpPopup = itemConfig => (
      <div className={`reject-nav-item ${readOnly ? 'disabled' : 'enabled'}`} >
        <div className="reject-masker" visible={readOnly} />
        <div
          id={itemConfig.id}
          role="button"
          tabIndex={0}
          onClick={showRejectPopup}
        >
          <span className={classNames(
            'iconba',
            {
              'iconba-RemoveContent': isDismissAll,
              'iconba-Blocked': !isDismissAll,
            }
          )}
          />
          <span>{itemConfig.text}</span>
        </div>
        {!isDismissAll &&
          <HelpPopup
            topic="pop_BA_Recommendations_StopShowingRec"
            aria-label={i18n.getString('LearnMoreLinkText')}
          />
        }
      </div >
    );

    if (_.result(context, 'Category', '') === RECOMMENDATION_CATEGORIES.DISMISSED) {
      gridToolbarGroup.toolbarWithItem('button-undismiss-all').update({
        id: 'button-undismiss-all',
        onClick: (e) => {
          e.stopPropagation();
          gridService.undismissAll({ recommendationType, context: { trackId: generateGuid() } })
            .then(() => {
              gridView.refresh({ force: true });
            }).finally(withUiBlock(el));

          return true;
        },
      });
    } else if (_.contains(SUPPORT_DISMISS_ALL_TYPES, recommendationType)) {
      gridToolbarGroup.toolbarWithItem('reject-help-popup').update({
        id: 'reject-help-popup',
        content: rejectHelpPopup({
          type: 'nav-item',
          id: 'button-reject',
          iconLeftGlyphName: 'ba-Blocked',
          text: isDismissAll ? i18n.getString('Details_View_Dismiss_All') : i18n.getString('Details_View_Reject'),
          readOnly,
        }),
      });
    }
  }
});

